import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { delay, formatDay, PageHead, STAGGER } from '@caios/ui';

type Person = { userId: string; name: string; email: string; role: string; accountIds: string[]; secondFactor: string; banned: boolean; lastActive: string | null };
type Invite = { id: string; email: string; role: string; accountIds: string[]; expiresAt: string; invitedBy: string | null };
type Data = { people: Person[]; invites: Invite[]; accounts: { id: string; name: string }[]; inviteBase: string };

const ROLE_LABEL: Record<string, string> = {
  owner: 'Owner', admin: 'Admin', manager: 'Manager', specialist: 'Specialist', viewer: 'Viewer',
  partner_admin: 'Partner admin', partner_user: 'Partner user', client_admin: 'Client admin', client_user: 'Client user',
};
const ROLE_GROUPS = [
  { label: 'Team', roles: ['admin', 'manager', 'specialist', 'viewer'] },
  { label: 'Partner', roles: ['partner_admin', 'partner_user'] },
  { label: 'Client', roles: ['client_admin', 'client_user'] },
];

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  // JSON content-type only when there is a body: an empty body with that header is rejected (Fastify).
  const headers = init?.body ? { 'content-type': 'application/json', ...(init.headers ?? {}) } : init?.headers;
  const r = await fetch(path, { credentials: 'include', ...init, ...(headers ? { headers } : {}) });
  const body = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(body.error ?? `Request failed (${r.status})`);
  return body as T;
}

export function People() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['people'], queryFn: () => api<Data>('/api/v1/admin/people') });
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('specialist');
  const [accountIds, setAccountIds] = useState<string[]>([]);
  const [link, setLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const create = useMutation({
    mutationFn: () => api<{ url: string }>('/api/v1/admin/invitations', { method: 'POST', body: JSON.stringify({ email, role, accountIds }) }),
    onSuccess: (r) => { setLink(r.url); setCopied(false); setEmail(''); setAccountIds([]); qc.invalidateQueries({ queryKey: ['people'] }); },
  });
  const cancel = useMutation({
    mutationFn: (id: string) => api(`/api/v1/admin/invitations/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['people'] }),
  });
  const [editing, setEditing] = useState<{ userId: string; ids: string[] } | null>(null);
  const access = useMutation({
    mutationFn: (e: { userId: string; ids: string[] }) => api(`/api/v1/admin/people/${e.userId}/accounts`, { method: 'PUT', body: JSON.stringify({ accountIds: e.ids }) }),
    onSuccess: () => { setEditing(null); qc.invalidateQueries({ queryKey: ['people'] }); },
  });
  const resend = useMutation({ mutationFn: (id: string) => api<{ emailed: boolean }>(`/api/v1/admin/invitations/${id}/resend`, { method: 'POST', body: '{}' }) });
  const remove = useMutation({
    mutationFn: (userId: string) => api(`/api/v1/admin/people/${userId}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['people'] }),
  });

  const acctName = (id: string) => q.data?.accounts.find((a) => a.id === id)?.name ?? '—';
  const scope = (ids: string[]) => (ids.length ? ids.map(acctName).join(', ') : 'No client accounts');

  if (q.isError) return <section className="cx-panel page"><p className="auth-error">{(q.error as Error).message}</p></section>;

  return (
    <div className="cx-stack">
      <PageHead section="Admin" about="who can get in, and what they see" title="People" />
      <section className="cx-hero-panel page cx-rise">
        <form className="invite-form" onSubmit={(e: FormEvent) => { e.preventDefault(); create.mutate(); }}>
          <label className="field"><span className="cx-label">Email</span>
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="invite-email" /></label>
          <label className="field"><span className="cx-label">Role</span>
            <select value={role} onChange={(e) => setRole(e.target.value)} data-testid="invite-role">
              {ROLE_GROUPS.map((g) => <optgroup key={g.label} label={g.label}>{g.roles.map((r) => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}</optgroup>)}
            </select></label>
          <fieldset className="field accounts-pick"><legend className="cx-label">Client accounts they can see</legend>
            <div className="chips">
              {q.data?.accounts.map((a) => (
                <label key={a.id} className={`chip${accountIds.includes(a.id) ? ' chip--on' : ''}`}>
                  <input type="checkbox" checked={accountIds.includes(a.id)} data-testid={`invite-account-${a.name}`}
                         onChange={(e) => setAccountIds(e.target.checked ? [...accountIds, a.id] : accountIds.filter((x) => x !== a.id))} />
                  {a.name}
                </label>
              ))}
            </div>
            <span className="cx-label">None picked = no client data until you grant some.</span>
          </fieldset>
          {create.isError && <p className="auth-error" role="alert">{(create.error as Error).message}</p>}
          <button type="submit" className="cx-btn cx-btn--primary" disabled={create.isPending} data-testid="invite-submit">
            {create.isPending ? 'Creating…' : 'Create invitation'}
          </button>
        </form>
        {link && (
          <div className="cx-inset invite-link cx-rise" data-testid="invite-link-box">
            <span className="cx-label">Send this link. It works once and expires in 7 days.</span>
            <code data-testid="invite-link">{link}</code>
            <button type="button" className="cx-btn" onClick={() => { navigator.clipboard?.writeText(link); setCopied(true); }}>{copied ? 'Copied' : 'Copy link'}</button>
          </div>
        )}
      </section>

      <section className="cx-panel page cx-rise" style={delay(1, STAGGER.list)}>
        <div className="cx-eyebrow">Members · <b>{q.data?.people.length ?? '…'}</b></div>
        <ul className="rows">
          {q.data?.people.map((p, i) => (
            <li key={p.userId} className="cx-inset row cx-rise" style={delay(i, STAGGER.list)} data-testid="person-row">
              <div><b>{p.name}</b> <span className="cx-label">{p.email}</span></div>
              <div className="cx-label">{ROLE_LABEL[p.role] ?? p.role} · {scope(p.accountIds)}</div>
              <span className={`pill pill--${p.secondFactor === 'none' ? 'warn' : 'good'}`}>{p.secondFactor === 'none' ? 'no second factor' : p.secondFactor}</span>
              {p.role !== 'owner' && <button type="button" className="row-action" onClick={() => setEditing(editing?.userId === p.userId ? null : { userId: p.userId, ids: p.accountIds })} data-testid="person-clients">Clients</button>}
              {p.role !== 'owner' && <button type="button" className="row-action" onClick={() => { if (confirm(`Remove ${p.name}? They’ll be signed out everywhere.`)) remove.mutate(p.userId); }} data-testid="person-remove">Remove</button>}
              {editing?.userId === p.userId && (
                <fieldset className="field accounts-pick" style={{ gridColumn: '1 / -1' }}><legend className="cx-label">Clients {p.name} can see — their tasks, Work and reports</legend>
                  <div className="chips">
                    {q.data?.accounts.map((a) => (
                      <label key={a.id} className={`chip${editing.ids.includes(a.id) ? ' chip--on' : ''}`}>
                        <input type="checkbox" checked={editing.ids.includes(a.id)} data-testid={`person-account-${a.name}`}
                               onChange={(e) => setEditing({ ...editing, ids: e.target.checked ? [...editing.ids, a.id] : editing.ids.filter((x) => x !== a.id) })} />
                        {a.name}
                      </label>
                    ))}
                  </div>
                  {access.isError && <p className="auth-error" role="alert">{(access.error as Error).message}</p>}
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button type="button" className="cx-btn cx-btn--primary cx-btn--sm" disabled={access.isPending} onClick={() => access.mutate(editing)} data-testid="person-clients-save">Save</button>
                    <button type="button" className="cx-btn cx-btn--sm" onClick={() => setEditing(null)}>Cancel</button>
                  </div>
                </fieldset>)}
            </li>
          ))}
        </ul>
        {!!q.data?.invites.length && (<>
          <div className="cx-eyebrow">Pending invitations · <b>{q.data.invites.length}</b></div>
          <ul className="rows">
            {q.data.invites.map((v) => (
              <li key={v.id} className="cx-inset row" data-testid="invite-row">
                <div><b>{v.email}</b></div>
                <div className="cx-label">{ROLE_LABEL[v.role] ?? v.role} · {scope(v.accountIds)} · expires {formatDay(v.expiresAt)}</div>
                <button type="button" className="row-action" onClick={() => resend.mutate(v.id)} disabled={resend.isPending} data-testid="invite-resend">{resend.isSuccess && resend.variables === v.id ? 'Sent' : 'Resend'}</button>
                <button type="button" className="row-action" onClick={() => cancel.mutate(v.id)} data-testid="invite-cancel">Cancel</button>
              </li>
            ))}
          </ul>
        </>)}
      </section>
    </div>
  );
}
